// DictionaryService.js

import { httpsCallable } from 'firebase/functions';

import { functions } from 'firebaseConfig';
import { normalizeRollupDefinitions } from 'rollups/rollupDefinitions';

const DEFAULT_FALLBACK_VIEW_TYPE = 'LIST';

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

function normalizeDefaultView(value) {
  const normalized = String(value || '').trim().toUpperCase();
  return normalized || DEFAULT_FALLBACK_VIEW_TYPE;
}

function normalizeVariantView(raw) {
  return {
    description: String(raw?.description || '').trim(),
    title: String(raw?.title || '').trim() || 'Untitled View',
    type: String(raw?.type || DEFAULT_FALLBACK_VIEW_TYPE).trim().toUpperCase() || DEFAULT_FALLBACK_VIEW_TYPE,
    boardField: String(raw?.boardField || '').trim(),
    boardEnumOptions: raw?.boardEnumOptions && typeof raw.boardEnumOptions === 'object'
      ? raw.boardEnumOptions
      : null,
    timelineField: String(raw?.timelineField || '').trim(),
    timelineRowMode: String(raw?.timelineRowMode || '').trim() || 'flat',
    timelineGroupField: String(raw?.timelineGroupField || '').trim(),
    showUnscheduled: raw?.showUnscheduled == null ? true : Boolean(raw?.showUnscheduled),
    chartConfig: raw?.chartConfig && typeof raw.chartConfig === 'object'
      ? raw.chartConfig
      : null,
    properties: asArray(raw?.properties),
    filters: asArray(raw?.filters),
    sorts: asArray(raw?.sorts),
    includeDescendants: Boolean(raw?.includeDescendants),
    includeDescription: Boolean(raw?.includeDescription),
    includeCover: Boolean(raw?.includeCover),
  };
}

function normalizeVariantSchemaField(raw) {
  return {
    ...raw,
    includeByDefault: raw?.includeByDefault !== false,
  };
}

function normalizeVariantAutomationAction(raw) {
  return {
    ...raw,
    key: String(raw?.key || '').trim(),
    type: String(raw?.type || '').trim().toLowerCase() || 'set_field',
    fieldKey: String(raw?.fieldKey || '').trim(),
    target: raw?.target && typeof raw.target === 'object'
      ? {
        ...raw.target,
        kind: String(raw?.target?.kind || '').trim() || 'current_record',
        databaseKey: String(raw?.target?.databaseKey || '').trim(),
        nodeTypeKey: String(raw?.target?.nodeTypeKey || '').trim(),
      }
      : { kind: 'current_record', databaseKey: '', nodeTypeKey: '' },
    value: raw?.value && typeof raw.value === 'object'
      ? { ...raw.value }
      : { type: 'literal', literal: raw?.value ?? '' },
  };
}

function normalizeVariantAutomationRule(raw) {
  return {
    key: String(raw?.key || '').trim(),
    enabled: raw?.enabled !== false,
    title: String(raw?.title || '').trim(),
    description: String(raw?.description || '').trim(),
    scope: raw?.scope && typeof raw.scope === 'object'
      ? {
        ...raw.scope,
        databaseKey: String(raw?.scope?.databaseKey || '').trim(),
        nodeTypeKey: String(raw?.scope?.nodeTypeKey || '').trim(),
      }
      : { databaseKey: '', nodeTypeKey: '' },
    trigger: raw?.trigger && typeof raw.trigger === 'object'
      ? {
        ...raw.trigger,
        type: String(raw?.trigger?.type || '').trim() || 'field_changed',
        fieldKey: String(raw?.trigger?.fieldKey || '').trim(),
      }
      : { type: 'field_changed', fieldKey: '' },
    conditionGroups: asArray(raw?.conditionGroups).map((group) => ({
      ...group,
      key: String(group?.key || '').trim(),
      conditions: asArray(group?.conditions).map((condition) => ({
        ...condition,
        key: String(condition?.key || '').trim(),
        fieldKey: String(condition?.fieldKey || '').trim(),
        operator: String(condition?.operator || '').trim(),
        value: condition?.value && typeof condition.value === 'object'
          ? { ...condition.value }
          : { type: 'literal', literal: condition?.value ?? '' },
      })),
    })),
    conditions: asArray(raw?.conditions).map((condition) => ({
      ...condition,
      key: String(condition?.key || '').trim(),
      fieldKey: String(condition?.fieldKey || '').trim(),
      operator: String(condition?.operator || '').trim(),
      value: condition?.value && typeof condition.value === 'object'
        ? { ...condition.value }
        : { type: 'literal', literal: condition?.value ?? '' },
    })),
    actions: asArray(raw?.actions).map((action) => normalizeVariantAutomationAction(action)),
  };
}

export function normalizeDictionaryTermKey(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .replace(/_+/g, '_');
}

function normalizeVariantPayload(raw) {
  const schemaFields = asArray(raw?.schema?.fields).map((field) => normalizeVariantSchemaField(field));

  return {
    key: String(raw?.key || '').trim(),
    title: String(raw?.title || '').trim() || 'Variant',
    description: String(raw?.description || '').trim(),
    defaultView: normalizeDefaultView(raw?.defaultView),
    label: String(raw?.label || '').trim(),
    labelPlural: String(raw?.labelPlural || '').trim(),
    iconKey: String(raw?.iconKey || '').trim() || 'deployed_code',
    rollups: normalizeRollupDefinitions(raw?.rollups),
    views: asArray(raw?.views).map((view) => normalizeVariantView(view)),
    automations: {
      rules: asArray(raw?.automations?.rules).map((rule) => normalizeVariantAutomationRule(rule)),
    },
    schema: {
      fields: schemaFields,
    },
    selectionCount: Number(raw?.selectionCount || 0),
    dateCreated: raw?.dateCreated || null,
    dateModified: raw?.dateModified || null,
  };
}

function normalizeTermPayload(raw) {
  const variants = asArray(raw?.variants).map((variant) => normalizeVariantPayload(variant));
  const singular = String(raw?.label || '').trim() || String(raw?.key || '').trim();
  const fallbackPlural = String(variants[0]?.labelPlural || '').trim() || `${singular}s`;
  const labelPlural = String(raw?.labelPlural || raw?.pluralLabel || '').trim() || fallbackPlural;

  return {
    key: String(raw?.key || '').trim(),
    label: singular,
    labelLower: String(raw?.labelLower || '').trim() || normalizeDictionaryTermKey(raw?.label || raw?.key),
    labelPlural,
    labelPluralLower: String(raw?.labelPluralLower || '').trim() || normalizeDictionaryTermKey(labelPlural),
    searchTokens: asArray(raw?.searchTokens),
    variantsCount: Number(raw?.variantsCount || variants.length || 0),
    selectionCount: Number(raw?.selectionCount || 0),
    dateCreated: raw?.dateCreated || null,
    dateModified: raw?.dateModified || null,
    variants,
  };
}

export async function listDictionaryEntries({ includeVariants = true } = {}) {
  const fn = httpsCallable(functions, 'listDictionaryEntries');
  const { data } = await fn({ includeVariants: includeVariants !== false });
  const terms = asArray(data?.terms).map((term) => normalizeTermPayload(term));
  return terms;
}

export async function publishWorkspaceTypeToDictionary({ workspaceKey, typeKey, title, description } = {}) {
  const fn = httpsCallable(functions, 'publishWorkspaceTypeToDictionary');
  const { data } = await fn({ workspaceKey, typeKey, title, description });
  return data || null;
}

export async function updateDictionaryVariant({ termKey, variantKey, title, description, defaultView, rollups, views, schemaFields, automations } = {}) {
  const fn = httpsCallable(functions, 'updateDictionaryVariant');
  const { data } = await fn({ termKey, variantKey, title, description, defaultView, rollups, views, schemaFields, automations });
  return data || null;
}

export async function deleteDictionaryVariant({ termKey, variantKey } = {}) {
  const fn = httpsCallable(functions, 'deleteDictionaryVariant');
  const { data } = await fn({ termKey, variantKey });
  return data || null;
}

export async function provisionDictionaryVariant({ workspaceKey, termKey, variantKey, selectedFieldKeys } = {}) {
  const fn = httpsCallable(functions, 'provisionDictionaryVariant');
  const { data } = await fn({ workspaceKey, termKey, variantKey, selectedFieldKeys });
  return data || null;
}
